/**
 * Shared section wrapper for the Entity Details panel.
 *
 * Renders the section header + body shell that every section inside the
 * consolidated Entity Details panel uses. The header is the "sticky
 * section header" called out in ADR-024 §4 — when the panel scrolls
 * past a section's header it pins to the top of the panel body until the
 * next section's header crosses.
 *
 * Each section is **collapsible** (REQ-UI-064 + REQ-UI-092 expand): the
 * header's title is a disclosure ``<button>`` (chevron + title) that
 * shows/hides the section body. The initial collapsed state and the
 * persisted toggle come from the per-panel
 * :func:`EntityDetailsSectionsContext` — the shell reads its own ``id``
 * off ``isExpanded`` / ``setExpanded`` so each panel provides the
 * context once and the shells self-wire. A shell rendered with **no**
 * provider in the tree (e.g. an isolated unit test) degrades to a plain,
 * always-open, non-collapsible header — the disclosure is additive and
 * never regresses a context-less caller.
 *
 * Each section receives:
 *   - a stable ``id`` (used by the jump-to-section affordance — PR 5.4 —
 *     and as the key into the section context's expand state).
 *   - a ``title`` (the all-caps section name from the ADR/Flow).
 *   - optional ``actions`` rendered in the section header's right side
 *     (e.g., "View all messages →"). Actions sit **beside** the
 *     disclosure button (not inside it), so they are independently
 *     clickable and never toggle collapse — an explicit
 *     ``stopPropagation`` on the actions wrapper is belt-and-suspenders.
 *   - ``children`` — the section body content.
 *
 * Accessibility (REQ-UI-094 / REQ-UI-059): the toggle is a real
 * ``<button type="button">`` with ``aria-expanded`` reflecting the open
 * state and ``aria-controls`` pointing at the body element; it is
 * keyboard-operable (native Enter/Space) and the chevron is
 * ``aria-hidden``.
 *
 * Cross-references:
 *   - REQ-UI-064 (collapsible sections — persisted, all entity types).
 *   - REQ-UI-092 (per-type expand/collapse defaults).
 *   - REQ-UI-094 / REQ-UI-059 (a11y — keyboard + aria).
 *   - REQ-UI-049 (sticky section headers).
 *   - ADR-024 §4 / §6 (consolidated panel structure + section storage).
 *   - ADR-033 §10 (relocated section config); Flow Q §"Frame 2".
 */
import type { MouseEvent, ReactNode } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { useEntityDetailsSectionsContext } from "@/entity-details/entity-details-sections-context";

export interface SectionShellProps {
  /** Stable identifier — the jump-to-section anchor AND the key into the
   *  per-panel section context's expand/collapse state. */
  id: string;
  /** Display title (all-caps per Flow Q). */
  title: string;
  /** Optional inline action in the header (e.g., "View all →"). */
  actions?: ReactNode;
  /** Section body. */
  children: ReactNode;
}

export function SectionShell({
  id,
  title,
  actions,
  children,
}: SectionShellProps): React.ReactElement {
  const ctx = useEntityDetailsSectionsContext();
  // No provider ⇒ no persisted collapse state ⇒ render plain + always
  // open (a context-less caller, e.g. an isolated shell test). With a
  // provider, the open state is the entity type's per-section default.
  const collapsible = ctx !== null;
  const expanded = ctx === null ? true : ctx.sections.isExpanded(id);
  const bodyId = `entity-details-section-${id}-body`;

  const handleToggle = (): void => {
    if (ctx === null) return;
    void ctx.sections.setExpanded(id, !expanded);
  };

  // Actions live beside (not inside) the disclosure button, so a click on
  // them is already outside the toggle's hit target; stop propagation
  // anyway to harden against any future header restructure.
  const stopActionPropagation = (event: MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
  };

  return (
    <section
      id={`entity-details-section-${id}`}
      data-testid={`entity-details-section-${id}`}
      data-expanded={expanded}
      className="border-b border-border last:border-b-0"
    >
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-bg-surface px-4 py-2">
        {collapsible ? (
          <button
            type="button"
            onClick={handleToggle}
            aria-expanded={expanded}
            aria-controls={bodyId}
            data-testid={`entity-details-section-toggle-${id}`}
            className="-mx-1 flex min-w-0 items-center gap-1.5 rounded px-1 text-left hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          >
            {expanded ? (
              <CaretDown size={12} weight="bold" aria-hidden className="shrink-0 text-text-muted" />
            ) : (
              <CaretRight
                size={12}
                weight="bold"
                aria-hidden
                className="shrink-0 text-text-muted"
              />
            )}
            <h3 className="truncate text-caption font-medium uppercase tracking-wider text-text-secondary">
              {title}
            </h3>
          </button>
        ) : (
          <h3 className="text-caption font-medium uppercase tracking-wider text-text-secondary">
            {title}
          </h3>
        )}
        {actions !== undefined && (
          <div className="flex items-center gap-2" onClick={stopActionPropagation}>
            {actions}
          </div>
        )}
      </header>
      {expanded && (
        <div id={bodyId} className="px-4 py-3">
          {children}
        </div>
      )}
    </section>
  );
}

// ---- Section helpers --------------------------------------------

/**
 * Key/value row for inside a section body.
 *
 * Renders ``label`` on the left in muted color, ``value`` on the
 * right in primary color. ``value`` can be a string OR any
 * ReactNode (so callers can render badges, links, etc.).
 */
export function SectionRow({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 text-body-sm">
      <span className="text-text-muted">{label}</span>
      <span className="text-right text-text-primary">{value}</span>
    </div>
  );
}

/**
 * Placeholder row inside a section body indicating that the
 * content isn't yet available. Used by PR 5.3 for STATUS /
 * POSITION / CONNECTIVITY / RECENT EVENTS / RECENT ERRORS pending
 * follow-up endpoints + telemetry sources.
 */
export function SectionPlaceholder({ note }: { note: string }): React.ReactElement {
  return <p className="text-body-sm italic text-text-muted">{note}</p>;
}
